/**
 * Загрузка изображения — один компонент на аватар и на логотип проекта.
 *
 * Задача у них одна: показать текущее, дать выбрать новое, дать убрать. Два
 * почти одинаковых блока в двух файлах разошлись бы через месяц — по
 * сообщениям об ошибках, по подписям, по тому, где именно виден ход загрузки.
 *
 * Что делает сам компонент, а не вызывающая сторона:
 *
 *   1. Отсекает заведомо неподходящее до отправки. Не ради безопасности —
 *      её обеспечивает сервер, определяя тип по содержимому, — а ради
 *      скорости ответа: незачем гнать десять мегабайт, чтобы услышать отказ.
 *
 *   2. Показывает выбранное сразу, не дожидаясь ответа. Загрузка занимает
 *      секунду, и всё это время человек должен видеть, что выбрал именно то.
 */

import { ImageIcon, Loader2Icon, TrashIcon, UploadIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Тот же предел, что и на сервере. */
const MAX_BYTES = 5 * 1024 * 1024;

const ACCEPTED = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];

export function ImageUpload({
  preview,
  label,
  hint,
  round = false,
  onUpload,
  onClear,
  canClear,
  disabled = false,
}: {
  /** Что показать сейчас: готовый элемент, чтобы компонент не знал о людях и проектах. */
  preview: React.ReactNode;
  label: string;
  hint?: string;
  /** Круглая рамка для аватара, скруглённый квадрат для логотипа. */
  round?: boolean;
  onUpload: (file: File) => Promise<unknown>;
  onClear?: () => Promise<unknown>;
  canClear: boolean;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);

  // Ссылка на выбранный файл живёт в памяти вкладки, пока её не отпустят.
  useEffect(
    () => () => {
      if (chosen) URL.revokeObjectURL(chosen);
    },
    [chosen],
  );

  const pick = async (file: File | undefined) => {
    if (!file) return;

    if (file.size > MAX_BYTES) {
      toast.error(`Файл больше ${Math.round(MAX_BYTES / 1024 / 1024)} МБ`);
      return;
    }
    // Тип от браузера — подсказка, а не проверка: настоящую делает сервер по
    // содержимому. Здесь она нужна, чтобы не отправлять заведомо не то.
    if (file.type && !ACCEPTED.includes(file.type)) {
      toast.error('Поддерживаются PNG, JPG, SVG и WebP');
      return;
    }

    const local = URL.createObjectURL(file);
    setChosen(local);
    setBusy(true);
    try {
      await onUpload(file);
    } catch (error) {
      setChosen(null);
      URL.revokeObjectURL(local);
      toast.error(error instanceof ApiError ? error.message : 'Не удалось загрузить изображение');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const clear = async () => {
    if (!onClear) return;
    setBusy(true);
    try {
      await onClear();
      setChosen(null);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось убрать изображение');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div
        className={cn(
          'relative grid shrink-0 place-items-center overflow-hidden bg-muted/40',
          round ? 'size-16 rounded-full' : 'size-16 rounded-lg',
        )}
      >
        {chosen ? <img src={chosen} alt="" className="size-full object-cover" /> : preview}
        {busy ? (
          <span className="absolute inset-0 grid place-items-center bg-background/70">
            <Loader2Icon className="size-4 animate-spin" />
          </span>
        ) : null}
      </div>

      <div className="min-w-0 space-y-2">
        <input
          ref={input}
          type="file"
          accept={ACCEPTED.join(',')}
          className="sr-only"
          onChange={(event) => void pick(event.target.files?.[0])}
        />

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={busy || disabled}
            onClick={() => input.current?.click()}
          >
            {busy ? <Loader2Icon className="animate-spin" /> : <UploadIcon />}
            {label}
          </Button>

          {canClear && onClear ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              disabled={busy || disabled}
              onClick={() => void clear()}
            >
              <TrashIcon />
              Убрать
            </Button>
          ) : null}
        </div>

        <p className="flex items-center gap-1.5 text-2xs leading-relaxed text-muted-foreground">
          <ImageIcon className="size-3 shrink-0" />
          {hint ?? `PNG, JPG, SVG или WebP, до ${Math.round(MAX_BYTES / 1024 / 1024)} МБ`}
        </p>
      </div>
    </div>
  );
}
